import * as Adaptive from "adaptivecards";
import { TypedCardElementPeer, PropertySheet, PropertySheetCategory, StringPropertyEditor, ChoicePropertyEditor, IVersionedChoice, BooleanPropertyEditor } from "adaptivecards-designer";
import { handleValidationElement, initCustomRenderedElement, removeOtherPropertyConfigurations } from "./Utils/CustomElementUtils";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const teamProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "team", true);
const groupProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "group", true);
const hideTeamProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "hideTeam", false);
const hideGroupProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "hideGroup", false);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField", false);
export class Assignment extends Adaptive.TextInput {
    static readonly JsonTypeName = "Assignment";
    static readonly ElementId = "Ticket.Assignment";
    static readonly defaultLabel = "Assignment";
    static readonly teamLabel = "Team:";
    static readonly groupLabel = "Group:";
    static readonly assigneeLabel = "Assignee:";
    static readonly displayLabelProperty = displayLabelProperty;
    static readonly teamProperty = teamProperty;
    static readonly groupProperty = groupProperty;
    static readonly hideTeamProperty = hideTeamProperty;
    static readonly hideGroupProperty = hideGroupProperty;
    static readonly requireFieldProperty = requireFieldProperty;
    
    static displayChoices: any[];
    static teamChoices: any[];
    static groupChoices: any[];
    static defaultTeam: any;
    static defaultGroup: any;

    static filterGroups = (value) => {
        let filteredGroup = (value) ? Assignment.groupChoices.filter(f => f.TeamsAadObjectId == value) : Assignment.groupChoices;
        filteredGroup = filteredGroup.map(t => {
            return { name: t.Name, value: t.Id.toString()} as IVersionedChoice;
        });
        AssignmentPeer.updateGroups(filteredGroup);
    }

    //#region Schema

    @Adaptive.property(displayLabelProperty)
    get displayLabel(): string | undefined {
        return this.getValue(Assignment.displayLabelProperty);
    }

    set displayLabel(value: string | undefined) {
        this.setValue(Assignment.displayLabelProperty, value);
        this.label = value;
        this.updateLayout();
    }

    @Adaptive.property(teamProperty)
    get team(): string | undefined {
        return this.getValue(Assignment.teamProperty);
    }

    set team(value: string | undefined) {
        this.setValue(Assignment.teamProperty, value);
        if(Assignment.groupChoices){
            Assignment.filterGroups(value);
            this.setValue(Assignment.groupProperty, undefined);
        }
        this.updateLayout();
    }

    @Adaptive.property(groupProperty)
    get group(): string | undefined {
        return this.getValue(Assignment.groupProperty);
    }

    set group(value: string | undefined) {
        this.setValue(Assignment.groupProperty, value ?? "");
        this.updateLayout();
    }

    @Adaptive.property(hideTeamProperty)
    get hideTeam(): boolean | undefined {
        return this.getValue(Assignment.hideTeamProperty);
    }

    set hideTeam(value: boolean | undefined) {
        this.setValue(Assignment.hideTeamProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(hideGroupProperty)
    get hideGroup(): boolean | undefined {
        return this.getValue(Assignment.hideGroupProperty);
    }

    set hideGroup(value: boolean | undefined) {
        this.setValue(Assignment.hideGroupProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(requireFieldProperty)
    get requireField(): boolean | undefined {
        return this.getValue(Assignment.requireFieldProperty);
    }

    set requireField(value: boolean | undefined) {
        this.setValue(Assignment.requireFieldProperty, value);
        this.updateLayout();
    }

    //#endregion

    protected internalRender(): HTMLElement | undefined {
        this.id = Assignment.ElementId;
        if((this?.displayLabel ?? "").length == 0)
            this.displayLabel = Assignment.defaultLabel;

        if(this.team)
            AssignmentPeer.selectedTeam = this.team;

        if(!this.team && (typeof Assignment.defaultTeam === 'object' && Assignment.defaultTeam !== null) ){
            const defaultTeam = Assignment.defaultTeam;
            this.setValue(Assignment.teamProperty, defaultTeam.TeamsAadObjectId);
            if(Assignment.groupChoices && this.team){
                Assignment.filterGroups(this.team);
            }
        }

        let element = initCustomRenderedElement("tikit-input-assignment");

        let textBlock1 = new Adaptive.FactSet();
        textBlock1.facts = [];
        
        if(!this.hideTeam)
            textBlock1.facts.push({name: Assignment.teamLabel, value: this.team ? Assignment.teamChoices?.find(x => x.TeamsAadObjectId == this.team)?.FriendlyName ?? "None" : "None"} as Adaptive.Fact);

        if(!this.hideGroup){
            const defaultText = (typeof Assignment.defaultGroup === 'object' && Assignment.defaultGroup !== null) ? Assignment.defaultGroup.Name : "(default)";
            textBlock1.facts.push({name: Assignment.groupLabel, value: !this.group || this.group == "-1" ? "None" : Assignment.groupChoices?.find(x => x.Id.toString() == this.group)?.Name ?? defaultText} as Adaptive.Fact);
        }

        let choiceSetInput = new Adaptive.ChoiceSetInput();
        choiceSetInput.setParent(this);
        if(!(this.hideTeam && this.hideGroup))
            choiceSetInput.label = Assignment.assigneeLabel;
        
        let choiceItems = Assignment.displayChoices ?? [];
        if(!choiceItems.some(c => c.value === "-1") && !(this.requireField || this.isRequired))
            choiceItems.unshift({ title: "Unassigned", value: "-1"} as Adaptive.Choice);
        choiceSetInput.choices = choiceItems;
        choiceSetInput.isRequired = this.requireField;
        choiceSetInput.onValueChanged = (sender) => {
            this.setValue(Assignment.valueProperty, sender.value);
            handleValidationElement(element);
        }

        if(this.defaultValue)
            choiceSetInput.defaultValue = this.defaultValue;
        
        const renderedElement =  choiceSetInput.render();

        const teamEl =  textBlock1.render();

        if(this.hideTeam && this.hideGroup)
            element.append(renderedElement);
        else
            element.append(teamEl, renderedElement);

        return element;
    }

    getJsonTypeName(): string {
        return Assignment.JsonTypeName;
    }

    get value(): string {
        return this.getValue(Assignment.valueProperty);
    }

    updateLayout(processChildren: boolean = true) {
        super.updateLayout(processChildren);
    }
}

export class AssignmentPeer extends TypedCardElementPeer<Assignment> {
    static readonly displayLabelProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "displayLabel", "Label", false);
    static teamProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "team", "Team", [], true);
    static groupProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "group", "Group", [], false);
    static hideTeamProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "hideTeam", "Hide Team", false);
    static hideGroupProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "hideGroup", "Hide Group", false);
    static requireFieldProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "requireField", "Required", false);

    static selectedTeam: any;

    static updateChoices(teamChoices: IVersionedChoice[], groupChoices: IVersionedChoice[]) {
        this.teamProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "team", "Team", teamChoices, true);
        if(!this.selectedTeam){
            AssignmentPeer.updateGroups(groupChoices);
        }else
            Assignment.filterGroups(this.selectedTeam);
    }

    static updateGroups(groupChoices: IVersionedChoice[]) {
            groupChoices.unshift({ name: "(default)", value: "0" } as IVersionedChoice);
            groupChoices.unshift({ name: "(not set)", value: "-1" } as IVersionedChoice);
        this.groupProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "group", "Group", groupChoices, false);
    }

    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);

        propertySheet.add(
            defaultCategory,
            AssignmentPeer.idProperty,
            AssignmentPeer.displayLabelProperty,
            AssignmentPeer.requireFieldProperty,
            AssignmentPeer.teamProperty,
            AssignmentPeer.hideTeamProperty,
            AssignmentPeer.groupProperty,
            AssignmentPeer.hideGroupProperty
        );
    }
}